import ContentLoader, { Rect } from "react-content-loader/native";
import { Card, HStack, VStack } from "@gluestack-ui/themed";
import { useDimensions } from "@repo/core/hooks/useDimensions";

const SKELETON_BG = "#E4E4E7";
const SKELETON_FG = "#F4F4F5";

interface SkeletonBlockProps {
  height: number | string;
  width?: number | string;
  borderRadius?: number;
}

const SkeletonBlock = ({
  height,
  width = "100%",
  borderRadius = 8
}: SkeletonBlockProps) => (
  <VStack h={height} w={width}>
    <ContentLoader
      backgroundColor={SKELETON_BG}
      foregroundColor={SKELETON_FG}
      speed={1}
      width="100%"
      height="100%"
    >
      <Rect
        x="0"
        y="0"
        rx={borderRadius}
        ry={borderRadius}
        width="100%"
        height="100%"
      />
    </ContentLoader>
  </VStack>
);

const ProposalDetailsCardSkeleton = ({
  showHeader
}: {
  showHeader: boolean;
}) => (
  <Card
    flex={3}
    borderRadius="$lg"
    borderWidth={showHeader ? 1 : 0}
    borderColor="$backgroundLight200"
    p="$0"
  >
    {showHeader ? (
      <HStack
        borderBottomWidth="$2"
        alignItems="center"
        p="$2"
        pl="$4"
        borderBottomColor="$backgroundLight200"
      >
        <SkeletonBlock height={24} width="40%" borderRadius={4} />
        <SkeletonBlock height={28} width={100} borderRadius={6} />
      </HStack>
    ) : null}
    <VStack p="$4" gap="$6">
      <HStack gap="$2">
        <VStack flex={1} gap="$2">
          <SkeletonBlock height={12} width="50%" borderRadius={4} />
          <SkeletonBlock height={28} width="80%" borderRadius={6} />
        </VStack>
        <VStack flex={1} gap="$2" alignItems="flex-end">
          <SkeletonBlock height={12} width="60%" borderRadius={4} />
          <SkeletonBlock height={36} width={120} borderRadius={8} />
        </VStack>
      </HStack>

      <VStack gap="$3">
        {Array.from({ length: 3 }).map((_, index) => (
          <VStack key={index} gap="$1">
            <SkeletonBlock height={12} width="25%" borderRadius={4} />
            <SkeletonBlock height={20} width="70%" borderRadius={4} />
          </VStack>
        ))}
      </VStack>

      <VStack gap="$1">
        <SkeletonBlock height={12} width="20%" borderRadius={4} />
        <SkeletonBlock height={56} width="100%" borderRadius={8} />
      </VStack>

      <VStack gap="$1">
        <SkeletonBlock height={12} width="35%" borderRadius={4} />
        <SkeletonBlock height={56} width="100%" borderRadius={8} />
      </VStack>
    </VStack>
  </Card>
);

export const ProposalDataTabSkeleton = () => (
  <VStack gap="$6" w="$full">
    <HStack gap="$2" w="$full">
      <VStack flex={1} gap="$2">
        <SkeletonBlock height={12} width="50%" borderRadius={4} />
        <SkeletonBlock height={28} width="80%" borderRadius={6} />
      </VStack>
      <VStack flex={1} gap="$2" alignItems="flex-end">
        <SkeletonBlock height={12} width="60%" borderRadius={4} />
        <SkeletonBlock height={36} width={120} borderRadius={8} />
      </VStack>
    </HStack>

    <VStack gap="$3">
      {Array.from({ length: 3 }).map((_, index) => (
        <VStack key={index} gap="$1">
          <SkeletonBlock height={12} width="25%" borderRadius={4} />
          <SkeletonBlock height={20} width="70%" borderRadius={4} />
        </VStack>
      ))}
    </VStack>

    <VStack gap="$1">
      <SkeletonBlock height={12} width="20%" borderRadius={4} />
      <SkeletonBlock height={56} width="100%" borderRadius={8} />
    </VStack>

    <VStack gap="$1">
      <SkeletonBlock height={12} width="35%" borderRadius={4} />
      <SkeletonBlock height={56} width="100%" borderRadius={8} />
    </VStack>
  </VStack>
);

export const AnalysisTabSkeleton = () => (
  <VStack gap="$6" w="$full">
    <HStack gap="$2" w="$full">
      <VStack flex={1} gap="$2">
        <SkeletonBlock height={12} width="50%" borderRadius={4} />
        <SkeletonBlock height={28} width="80%" borderRadius={6} />
      </VStack>
      <VStack flex={1} gap="$2" alignItems="flex-end">
        <SkeletonBlock height={12} width="60%" borderRadius={4} />
        <SkeletonBlock height={36} width={120} borderRadius={8} />
      </VStack>
    </HStack>
    <VStack gap="$2">
      <SkeletonBlock height={12} width="35%" borderRadius={4} />
      {Array.from({ length: 4 }).map((_, index) => (
        <SkeletonBlock key={index} height={48} borderRadius={8} />
      ))}
    </VStack>
  </VStack>
);

export const DocumentsListSkeleton = () => (
  <VStack gap="$10" w="$full">
    {Array.from({ length: 3 }).map((_, index) => (
      <VStack key={index} gap="$3">
        <SkeletonBlock height={12} width="30%" borderRadius={4} />
        <HStack gap="$2" alignItems="center">
          <SkeletonBlock height={32} width={32} borderRadius={4} />
          <VStack flex={1} gap="$2">
            <SkeletonBlock height={16} width="70%" borderRadius={4} />
            <SkeletonBlock height={12} width="50%" borderRadius={4} />
          </VStack>
        </HStack>
        <SkeletonBlock height={36} width="100%" borderRadius={8} />
      </VStack>
    ))}
  </VStack>
);

const DocumentsCardSkeleton = () => (
  <Card
    flex={1}
    borderRadius="$lg"
    bgColor="$backgroundLightMuted"
    borderWidth={0}
    p="$0"
  >
    <HStack
      borderBottomWidth="$2"
      alignItems="center"
      p="$2"
      pl="$4"
      borderBottomColor="$backgroundLight200"
    >
      <SkeletonBlock height={24} width="50%" borderRadius={4} />
    </HStack>
    <VStack p="$4" gap="$3">
      {Array.from({ length: 3 }).map((_, index) => (
        <SkeletonBlock key={index} height={48} borderRadius={8} />
      ))}
    </VStack>
  </Card>
);

export const ProposalsViewInfoLoading = () => {
  const { isMobile } = useDimensions();

  return (
    <VStack w="$full" gap="$4" pb="$4" bgColor="$backgroundLight0">
      {isMobile ? (
        <VStack alignItems="center" justifyContent="center" mb="$2">
          <SkeletonBlock height={28} width={120} borderRadius={6} />
        </VStack>
      ) : null}

      <HStack gap="$6" w="$full">
        <ProposalDetailsCardSkeleton showHeader={!isMobile} />

        {!isMobile ? (
          <VStack flex={1} gap="$4">
            <DocumentsCardSkeleton />
            <SkeletonBlock height={44} width="100%" borderRadius={8} />
          </VStack>
        ) : null}
      </HStack>
    </VStack>
  );
};
